<template>
  <pt-sheet class="report-container-wrapper">
    <div class="top-level-wrapper">
      <img
        class="top-right"
        style="margin-top: -6px"
        src="@/assets/blue-circle-accents.png"
      />
      <img class="bottom-left" src="@/assets/green-circle-accents.png" />
      <div class="report-container pb-sm-14 pt-5 px-sm-2 px-md-4 px-lg-6">
        <div class="download-button-container">
          <v-spacer></v-spacer>
          <cta-button
            v-if="!isStandAlonePocPage"
            @click="emailDialog = true"
            :style="{ visibility: downloading ? 'hidden' : 'visible' }"
            :disabled="!hasEMSubscription"
          >
            <v-icon left>mdi-email</v-icon>Share a Report
          </cta-button>
          <cta-button
            class="ml-4"
            @click="downloadPDF"
            :loading="downloading"
            :style="{ visibility: downloading ? 'hidden' : 'visible' }"
          >
            <v-icon left>mdi-download</v-icon>Download
          </cta-button>
          <cta-button
            class="ml-4"
            v-if="false"
            :style="{ visibility: downloading ? 'hidden' : 'visible' }"
          >
            <v-icon left>mdi-link</v-icon>Copy link
          </cta-button>
        </div>
        <h2 class="report-title">{{ reportName }}</h2>
        <div class="report-content py-sm-8 px-sm-2 px-md-8 px-lg-16">
          <img class="center mb-6" src="@/assets/pt-logo.png" />
          <p class="text-center mb-10">
            ProviderTrust’s platforms provide real-time monitoring of people and
            organizations who are excluded from receiving payments from
            Medicare, Medicaid and other federal health programs. As exclusions
            are identified, they are reported back to the vendor organization in
            real-time for corrective action.
          </p>
          <div class="mb-6">
            <h3 class="section-title">Vendor Information</h3>
            <v-divider></v-divider>
            <div class="py-sm-4">
              <div>
                <span class="label">Vendor Name: </span>
                {{ vendor.name }}
              </div>
              <div>
                <span class="label">Address: </span>
                <address class="d-inline-block" v-if="vendor.address">
                  <p>
                    {{ vendor.address.addrLine1 }}
                  </p>
                  <p>
                    {{ vendor.address.addrLine2 }}
                  </p>
                  <p>
                    {{ vendor.address.city }}, {{ vendor.address.state }}
                    {{ vendor.address.zip }}
                  </p>
                  <p>
                    {{ vendor.phoneNumber | formatPhone }}
                  </p>
                </address>
              </div>
              <div>
                <span class="label">VendorProof Engage Start Date: </span>
                {{ vendor.enrollmentDate | formatDate }} - present
              </div>
            </div>
          </div>
          <div>
            <h3 class="section-title">Monitoring Information</h3>
            <v-divider></v-divider>
            <div class="px-sm-2 px-md-6 px-lg-12 py-sm-8">
              <v-row
                v-for="(info, indx) in monitoringInfos"
                :key="info.type"
                no-gutters
                :class="indx != monitoringInfos.length - 1 && 'mb-8'"
              >
                <v-col md="3" cols="12" class="subtitle">{{ info.name }}</v-col>
                <v-col md="5" cols="12">
                  <img :src="info.logo" :style="`width: ${info.logoWidth}`" />
                  <p class="mb-4">
                    {{ info.desc }}
                  </p>
                  <a
                    target="_blank"
                    href="https://www.vendorproof.com/what-is-exclusion-monitoring/"
                    >View monitoring sources</a
                  >
                </v-col>
                <v-col md="3" offset-md="1" cols="12">
                  <div class="d-flex align-center">
                    <v-icon
                      class="mr-1"
                      color="pink lighten-3"
                      v-if="info.numberOfMatch > 0"
                      >mdi-alert-circle</v-icon
                    >
                    <v-icon
                      class="mr-1"
                      color="success"
                      v-else-if="info.numberOfMatch === 0"
                      >mdi-check-circle</v-icon
                    >
                    <v-icon
                      class="mr-1"
                      v-else-if="!info.numberOfMatch"
                      color="error"
                      >mdi-close-circle</v-icon
                    >
                    <span
                      v-if="info.numberOfMatch > 0 || info.numberOfMatch === 0"
                    >
                      {{ info.numberOfMatch || "No" }} matched exclusion{{
                        info.numberOfMatch === 1 ? "" : "s"
                      }}
                      found
                    </span>
                    <span v-else-if="!info.numberOfMatch">
                      Not monitored
                    </span>
                    <span v-else>Unknown Status</span>
                  </div>

                  <div v-if="info.startDate">
                    Monitoring Since {{ info.startDate | formatDate }}
                  </div>
                </v-col>
              </v-row>
            </div>
          </div>
        </div>
      </div>
      <shared-report-dialog v-model="emailDialog"> </shared-report-dialog>
    </div>
  </pt-sheet>
</template>

<script>
import { vendorInfoAPI } from "@/services/api";
import { vendorInfoByIdAPI } from "@/services/api";
import { datePickerNow } from "@/services/util";
import { get } from "@/services/request";
import { formatDate } from "@/services/format";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import vpLogo from "@/assets/vp-logo.png";
import vpEngageLogo from "@/assets/vpengage-logo.png";
import BlueCallToAction from "@/components/providertrust/buttons/BlueCallToAction";
import * as rules from "@/services/rules";
import hasNotifications from "@/mixins/has-notifications";
import SharedReportDialog from "@/views/reports/SharedReportDialog";
import PtSheet from "@/components/PtSheet.vue";
import { createNamespacedHelpers } from "vuex";
const { mapMutations, mapGetters } = createNamespacedHelpers("vendor");

const monitoringInfoMap = {
  COMPANY_MONITORING: {
    name: "Company Monitoring",
    desc:
      "VendorProof monitors all vendor data against all Federal (OIG LEIE and SAM/GSA) and all available State Medicaid exclusion lists.",
    logo: vpLogo,
    logoWidth: "196px"
  },
  OWNER_MONITORING: {
    name: "Owner Monitoring",
    desc:
      "VendorProof Engage captures and monitors owner information against all Federal and available State Medicaid exclusion lists.",
    logo: vpEngageLogo,
    logoWidth: "291px"
  },
  EMPLOYEE_MONITORING: {
    name: "Employee Monitoring",
    desc:
      "VendorProof Engage monitors all employee data against all Federal and all available State Medicaid exclusion lists.",
    logo: vpEngageLogo,
    logoWidth: "291px"
  }
};

const reportName = "Compliance Report";

export default {
  name: "ComplianceReport",
  props: ["vendorId"],
  data: () => ({
    vendor: {},
    reportName,
    downloading: false,
    emailDialog: false,
    rules,
    now: datePickerNow()
  }),

  created() {
    if (this.isStandAlonePocPage) {
      get(vendorInfoByIdAPI.getUrl(this.vendorId)).then(response => {
        if (response && response.data) this.vendor = response.data;
      });
    } else {
      get(vendorInfoAPI).then(response => {
        if (response && response.data) this.vendor = response.data;
      });
    }
  },
  components: {
    "cta-button": BlueCallToAction,
    SharedReportDialog,
    PtSheet
  },
  mixins: [hasNotifications],
  filters: {
    formatPhone: val =>
      val &&
      val.replace(/[^0-9]/g, "").replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3"),
    formatDate
  },
  methods: {
    ...mapMutations(["setEMSubscription"]),
    downloadPDF() {
      this.logEvent("Downloaded Report", {
        "Report Name": `${reportName} (${this.vendor.name || ""})`
      });

      const filename = `${reportName}.pdf`;
      const reportContainer = document.querySelector(
        ".report-container-wrapper"
      );
      // hide scroll bar temporarily: https://github.com/niklasvh/html2canvas/issues/1438#issuecomment-366727801
      const overflowVal = document.querySelector("html").style.overflow;
      document.querySelector("html").style.overflow = "hidden";

      this.downloading = true;
      this.$nextTick(() => {
        html2canvas(reportContainer, {
          scrollX: 0,
          scrollY: -window.pageYOffset,
          // fix grey area issue: https://github.com/niklasvh/html2canvas/issues/1937#issuecomment-520059242
          scale: 2
        })
          .then(canvas => {
            document.querySelector("html").style.overflow = overflowVal;
            let pdf = new jsPDF({
              orientation: "portrait",
              unit: "in",
              format: [8.5, 11],
              compress: true
            });
            const width = pdf.internal.pageSize.getWidth();
            const height = (canvas.height / canvas.width) * width;
            pdf.addImage(
              canvas.toDataURL("image/png"),
              "PNG",
              0,
              0,
              width,
              height
            );
            pdf.save(filename);
          })
          .catch(err => {
            console.error("Error creating PDF", err);
            document.querySelector("html").style.overflow = overflowVal;
          })
          .finally(() => {
            this.downloading = false;
          });
      });
    },
    closeEmailDialog() {
      this.emailDialog = false;
    }
  },
  computed: {
    isStandAlonePocPage() {
      return this.vendorId;
    },
    monitoringInfos() {
      if (!this.vendor.monitoringInfos || !this.vendor.monitoringInfos.length)
        return [];
      return this.vendor.monitoringInfos.map(monitoringInfo => ({
        ...monitoringInfoMap[monitoringInfo.type],
        ...monitoringInfo
      }));
    },
    ...mapGetters(["hasEMSubscription"])
  },
  watch: {
    monitoringInfos(val) {
      const emSubscription = val.find(
        ({ type, numberOfMatch }) =>
          type === "EMPLOYEE_MONITORING" && numberOfMatch !== null
      );
      this.setEMSubscription(emSubscription);
    }
  }
};
</script>

<style lang="sass" scoped>
.report-container-wrapper.card-with-highlight
  border-top-width: 6px
  padding: 0 !important
  max-width: 1185px

.top-level-wrapper
  border-top-width: 6px
  padding: 0 !important
  >img
    position: absolute
    &.top-right
      top: 0
      right: 0
    &.bottom-left
      left: 0
      bottom: 0
.report-container
  .report-title
    font-size: 40px
  .report-content
    img
      width: 494px
    font-size: 14px
    color: $darker-grey
    .section-title
      font-size: 20px
    .subtitle
      color: $subtitle
      font-weight: 600
    .label
      font-weight: bold
      vertical-align: top
      whitespace: pre
    address
      font-style: normal
      p
        margin: 0
.download-button-container
  display: flex
  justify-content: right
</style>
